import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { CATEGORIES } from "../../lib/constants";
import { Field } from "./Field";

const AD_SLOTS = [
  { id: "leaderboard", label: "Leaderboard (below hero) · 728×90" },
  { id: "rectangle", label: "Rectangle (sidebar, top) · 300×250" },
  { id: "sidebar_tall", label: "Sidebar Half-Page · 300×600" },
  { id: "sidebar_sticky", label: "Sidebar Sticky (bottom) · 300×250" },
  { id: "spotlight_sponsor", label: "Spotlight Sponsor (Business & Tourism · text label)" },
  { id: "essentials_pharmacy_sponsor",     label: "Essentials — Pharmacy Sponsor (text)" },
  { id: "essentials_beach_sponsor",        label: "Essentials — Beach Sponsor (text)" },
  { id: "essentials_currency_sponsor",     label: "Essentials — Currency Sponsor (text)" },
  { id: "essentials_cruise_sponsor",       label: "Essentials — Cruise Sponsor (text)" },
  { id: "sponsored_category", label: "Sponsored Category Label" },
];

const SPONSORABLE_CATEGORIES = CATEGORIES.filter((c) => c !== "All");

const EMPTY_FORM = {
  image_url: "",
  link_url: "",
  sponsor_name: "",
  tagline: "",
  category: "",
  island_targeting: "",
  active: true,
};

const AdForm = ({ slot, label, creatives, onSave, onDelete }) => {
  const isCategorySlot = slot === "sponsored_category";

  const [islandVariants, setIslandVariants] = useState([
    { key: "all", label: "🌐 All islands (fallback)" },
  ]);
  useEffect(() => {
    let cancelled = false;
    api.get("/islands").then((r) => {
      if (cancelled) return;
      const list = [{ key: "all", label: "🌐 All islands (fallback)" }];
      for (const d of r.data || []) {
        list.push({
          key: d.slug,
          label: `${d.flag_emoji || ""} ${d.name}`.trim(),
        });
      }
      setIslandVariants(list);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const variants = isCategorySlot
    ? SPONSORABLE_CATEGORIES.map((c) => ({ key: c, label: c }))
    : islandVariants;

  const [variant, setVariant] = useState(variants[0].key);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    const match = creatives.find((c) =>
      isCategorySlot
        ? (c.category || "") === variant
        : ((c.island_targeting || "").toLowerCase() || "all") === variant
    );
    if (match) {
      setForm({
        image_url: match.image_url || "",
        link_url: match.link_url || "",
        sponsor_name: match.sponsor_name || "",
        tagline: match.tagline || "",
        category: match.category || (isCategorySlot ? variant : ""),
        island_targeting: match.island_targeting || (isCategorySlot ? "" : variant),
        active: match.active ?? true,
      });
    } else {
      setForm({
        ...EMPTY_FORM,
        category: isCategorySlot ? variant : "",
        island_targeting: isCategorySlot ? "" : variant,
      });
    }
  }, [variant, creatives, isCategorySlot]);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const filledCount = creatives.filter((c) => c.sponsor_name || c.image_url).length;
  const hasContent = !!(form.sponsor_name || form.image_url);

  const [conflict, setConflict] = useState(null);
  const [checkingConflict, setCheckingConflict] = useState(false);

  const handleSaveClick = async () => {
    if (isCategorySlot || !form.active) {
      onSave(slot, form);
      return;
    }
    setCheckingConflict(true);
    try {
      const r = await api.get("/admin/ads/conflict-check", {
        params: { slot, island: form.island_targeting || "all" },
      });
      if (r.data?.conflict) {
        setConflict(r.data);
      } else {
        onSave(slot, form);
      }
    } catch {
      onSave(slot, form);
    } finally {
      setCheckingConflict(false);
    }
  };

  const confirmOverride = () => {
    setConflict(null);
    onSave(slot, form);
  };

  return (
    <div className="card-soft p-5" data-testid={`ad-card-${slot}`}>
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-[12px] uppercase tracking-wider text-[#0AADA0]">{label}</h3>
        <span className="text-[10px] text-[#6B7280]">
          {filledCount} of {variants.length} filled
        </span>
      </div>
      <p className="text-[11px] text-[#6B7280] mb-3">
        {isCategorySlot
          ? "Sell each editorial category to its own sponsor — pick a category, fill the creative, save."
          : "Sell this slot to a different advertiser per island, or use the “All islands” fallback."}
      </p>

      <div className="flex flex-wrap gap-1.5 mb-4" data-testid={`ad-${slot}-variants`}>
        {variants.map((v) => {
          const filled = creatives.some((c) => {
            const has = c.sponsor_name || c.image_url;
            if (!has) return false;
            return isCategorySlot
              ? (c.category || "") === v.key
              : ((c.island_targeting || "").toLowerCase() || "all") === v.key;
          });
          const active = variant === v.key;
          return (
            <button
              key={v.key}
              onClick={() => setVariant(v.key)}
              data-testid={`ad-${slot}-variant-${v.key}`}
              className={`text-[11px] px-2.5 py-1 rounded-full transition-colors flex items-center gap-1 ${
                active
                  ? "bg-[#0AADA0] text-white"
                  : "bg-[#F5F5F5] text-[#1A1A1A] hover:bg-[#EEEEEE]"
              }`}
            >
              {v.label}
              {filled && (
                <span
                  className={`w-1.5 h-1.5 rounded-full ${active ? "bg-white" : "bg-[#3BB273]"}`}
                  aria-label="has creative"
                />
              )}
            </button>
          );
        })}
      </div>

      <div className="space-y-3">
        <Field label="Sponsor Name"  value={form.sponsor_name} onChange={(v) => change("sponsor_name", v)} testid={`ad-${slot}-sponsor`} />
        <Field label="Tagline"       value={form.tagline}      onChange={(v) => change("tagline", v)}      testid={`ad-${slot}-tagline`} />
        <Field label="Image URL"     value={form.image_url}    onChange={(v) => change("image_url", v)}    testid={`ad-${slot}-image`} />
        <Field label="Link URL"      value={form.link_url}     onChange={(v) => change("link_url", v)}     testid={`ad-${slot}-link`} />
        <label className="flex items-center gap-2 text-xs text-[#6B7280]">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => change("active", e.target.checked)}
            data-testid={`ad-${slot}-active`}
            className="accent-[#0AADA0]"
          />
          Active
        </label>
        <div className="flex gap-2">
          <button
            onClick={handleSaveClick}
            disabled={checkingConflict}
            className="flex-1 py-2 rounded-full bg-[#E63946] text-white text-xs hover:bg-[#cf2f3c] transition-colors disabled:opacity-60"
            data-testid={`ad-${slot}-save`}
          >
            {checkingConflict ? "Checking…" : `Save ${variants.find((v) => v.key === variant)?.label}`}
          </button>
          {hasContent && (
            <button
              onClick={() => onDelete(slot, variant)}
              className="px-3 py-2 rounded-full bg-[#FAFAFA] text-[#E63946] text-xs hover:bg-[#FFEBEC] transition-colors border border-[#E5E5E5]"
              data-testid={`ad-${slot}-delete`}
              title="Remove this variant"
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
        {conflict && (
          <div
            className="mt-3 p-3 rounded-lg border border-[#F59E0B] bg-[#FFF7E6] text-[12px] text-[#78350F]"
            data-testid={`ad-${slot}-conflict-warning`}
          >
            <b>Warning:</b> A self-serve ad from <b>{conflict.business_name}</b> is currently active for this slot and island. Saving this manual ad will override it until the manual ad is removed. The self-serve order will resume automatically when the manual ad is deleted.
            <div className="mt-2 flex gap-2">
              <button
                onClick={confirmOverride}
                className="text-[11px] bg-[#F59E0B] text-white px-3 py-1 rounded-full"
                data-testid={`ad-${slot}-conflict-confirm`}
              >Confirm & save</button>
              <button
                onClick={() => setConflict(null)}
                className="text-[11px] text-[#78350F]"
                data-testid={`ad-${slot}-conflict-cancel`}
              >Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const InventoryOverview = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/ads/inventory")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);
  if (!data) return null;
  const cellStyle = (s) => {
    if (s === "manual")     return { bg: "#DCFCE7", fg: "#166534", label: "Manual" };
    if (s === "self_serve") return { bg: "#CCFBF1", fg: "#0F766E", label: "Self-Serve" };
    return { bg: "#F5F5F5", fg: "#6B7280", label: "Empty" };
  };
  const ISLAND_LABEL = { all: "All", aruba: "Aruba", curacao: "Curaçao",
                          bonaire: "Bonaire", stmaarten: "St. Maarten" };
  return (
    <div className="card-soft p-5" data-testid="ads-inventory-overview">
      <h3 className="text-[13px] uppercase tracking-wider text-[#0AADA0] mb-3">
        Inventory Overview
      </h3>
      <p className="text-[11px] text-[#6B7280] mb-3">
        Read-only snapshot of every ad slot × island. Manual admin ads take priority over self-serve creatives.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-[12px]" data-testid="inventory-grid">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-[#6B7280] border-b border-[#EEEEEE]">
              <th className="py-2 pr-3">Slot</th>
              {data.islands.map((i) => (
                <th key={i} className="py-2 pr-3">{ISLAND_LABEL[i] || i}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.slot_label} className="border-b border-[#F5F5F5]">
                <td className="py-1.5 pr-3 text-[#1A1A1A] whitespace-nowrap">{row.slot_label}</td>
                {data.islands.map((i) => {
                  const cell = row.cells[i] || { status: "empty", label: "" };
                  const st = cellStyle(cell.status);
                  return (
                    <td key={i} className="py-1.5 pr-3">
                      <span
                        style={{ background: st.bg, color: st.fg }}
                        className="inline-block text-[10px] font-medium px-2 py-0.5 rounded-full"
                        data-testid={`inv-${row.slot_label}-${i}`}
                        title={cell.label || st.label}
                      >
                        {st.label}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AdPerformanceCard = () => {
  const [rows, setRows] = useState([]);
  const [since, setSince] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("admin_token") || "";
    if (!backend || !token) { setLoading(false); return; }
    fetch(`${backend}/api/admin/ads/perf`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r.statusText))
      .then((data) => {
        setRows(data.rows || []);
        setSince(data.since || "");
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="mt-8" data-testid="admin-ads-perf">
      <h3 className="font-serif-display text-lg text-[#1A1A1A] mb-2">
        Sponsor Performance (last 30 days)
      </h3>
      <p className="text-xs text-[#6B7280] mb-3">
        Impressions/clicks per (slot, sponsor, island). Server-side impression
        counts from `/ads/carnival` responses + client-side pings, both
        bot-filtered. CTR = clicks / impressions.
        {since ? ` Since: ${since.slice(0, 10)}` : ""}
      </p>
      {loading && <p className="text-sm text-[#6B7280]">Loading…</p>}
      {error && <p className="text-sm text-[#991B1B]">Error: {error}</p>}
      {!loading && !error && rows.length === 0 && (
        <p className="text-sm text-[#6B7280]">
          No impressions or clicks recorded yet. Data will appear once a
          Carnival sponsor is live and visitors hit the page.
        </p>
      )}
      {!loading && rows.length > 0 && (
        <div className="border border-[#E5E7EB] rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-ads-perf-table">
            <thead className="bg-[#F9FAFB] text-left text-xs uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="px-3 py-2">Slot</th>
                <th className="px-3 py-2">Sponsor</th>
                <th className="px-3 py-2">Island</th>
                <th className="px-3 py-2 text-right">Impressions</th>
                <th className="px-3 py-2 text-right">Clicks</th>
                <th className="px-3 py-2 text-right">CTR</th>
              </tr>
            </thead>
            <tbody className="text-[#1A1A1A]">
              {rows.map((r, i) => (
                <tr
                  key={`${r.slot}-${r.sponsor_id || r.sponsor_name}-${r.island}-${i}`}
                  className="border-t border-[#E5E7EB]"
                  data-testid={`admin-ads-perf-row-${i}`}
                >
                  <td className="px-3 py-2 font-mono text-xs text-[#374151]">{r.slot}</td>
                  <td className="px-3 py-2">{r.sponsor_name || r.sponsor_id || "—"}</td>
                  <td className="px-3 py-2 capitalize">{r.island || "all"}</td>
                  <td className="px-3 py-2 text-right">{r.impressions}</td>
                  <td className="px-3 py-2 text-right">{r.clicks}</td>
                  <td className="px-3 py-2 text-right">
                    {(r.ctr * 100).toFixed(2)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export const AdsTab = ({ ads, onSave, onDelete }) => (
  <div data-testid="admin-ads">
    <InventoryOverview />
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
      {AD_SLOTS.map((s) => (
        <AdForm
          key={s.id}
          slot={s.id}
          label={s.label}
          creatives={ads[s.id] || []}
          onSave={onSave}
          onDelete={onDelete}
        />
      ))}
    </div>
    <AdPerformanceCard />
  </div>
);
